/* eslint-disable @typescript-eslint/naming-convention */
import {HttpClient, HttpHeaders} from '@angular/common/http'
import {Injectable} from '@angular/core'
import {Role, RolesEnum, User} from '@auth/models/user.model'
import {Observable} from 'rxjs'
import {SERVER_API_URL} from 'src/app/app.constants'
import {environment} from '../../../environments/environment'
import {AUTH_STORAGE_KEYS} from '../../core/auth/auth-keys'
import {isJwtExpired} from '../../core/auth/jwt'

/**
 * Token-Kontrakt der Anmeldung: set/get/remove für alle vier Artefakte.
 *
 * Die Schlüssel kommen aus `AUTH_STORAGE_KEYS`, damit die Übernahme in
 * `main.ts` und dieser Dienst dieselben Fächer benutzen.
 */
@Injectable({
  providedIn: 'root'
})

export class AuthService {
  private url = SERVER_API_URL

  private httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
      'x-apikey': environment.authKey
    })
  }

  constructor(private http: HttpClient) {
  }

  getLoginData(email: string, password: string): Observable<User> {
    return this.http.get<User>(
      `${this.url}/getLoginData?auth=${environment.authKey}&username=${email}&password=${password}`,
      this.httpOptions
    )
  }

  /**
   * Löst die verschlüsselte Kennung aus der Portal-Übergabe zum Benutzer auf.
   *
   * ⚠️ `token` ist hier NICHT der JWT, sondern der Wert des Parameters `user`.
   * Das Backend entschlüsselt daraus die Mailadresse.
   */
  getLoginDataFromAuthToken(token: string): Observable<User> {
    return this.http.get<User>(
      `${this.url}/getLoginDataFromAuthTokenAlternative?token=${token}`,
      this.httpOptions
    )
  }

  /**
   * Löst den Benutzer allein aus dem JWT auf.
   *
   * Ohne `token`-Parameter liest dasselbe Servlet den Kopf
   * `Authorization: Bearer` und ermittelt den Benutzer über den
   * `email`-Anspruch des Tokens.
   *
   * Gebraucht wird das, weil das Portal die Kennung NICHT immer mitschickt:
   * Schlägt dort `getAuthParameters` fehl, leitet es bewusst nur mit dem JWT
   * weiter ("Das JWT allein genügt den Werkzeugen zur Anmeldung"). Ohne diesen
   * Weg wären genau diese Benutzer ausgesperrt.
   */
  getLoginDataFromJwt(): Observable<User> {
    return this.http.get<User>(
      `${this.url}/getLoginDataFromAuthTokenAlternative`,
      this.httpOptions
    )
  }

  /**
   * Ermittelt den angemeldeten Benutzer auf dem Weg, der gerade möglich ist.
   * Kennung aus der Übergabe, sonst über den JWT.
   */
  resolveCurrentUser(): Observable<User> {
    const userParam = this.getUserParam()

    return userParam
      ? this.getLoginDataFromAuthToken(userParam)
      : this.getLoginDataFromJwt()
  }

  getAuthUserToken(): User | null {
    try {
      const authToken =
        JSON.parse(localStorage.getItem(AUTH_STORAGE_KEYS.user)) || (null as User | null)

      return authToken
    } catch {
      return null
    }
  }

  setAuthUserToken(user: User): void {
    localStorage.setItem(AUTH_STORAGE_KEYS.user, JSON.stringify(user))
  }

  setJWTToken(jwtToken: string) {
    localStorage.setItem(AUTH_STORAGE_KEYS.jwt, jwtToken)
  }

  /**
   * JWT aus dem Speicher.
   *
   * Die Abfrage auf die Zeichenfolgen `'null'` und `'undefined'` ist Pflicht:
   * Ohne sie wandert der Text `undefined` als Bearer-Token in den Header und
   * das Backend antwortet mit 401, obwohl überhaupt kein Token vorliegt.
   */
  getJWTToken(): string {
    const authToken = localStorage.getItem(AUTH_STORAGE_KEYS.jwt)
    if (!authToken || authToken === 'null' || authToken === 'undefined') {
      return ''
    }
    return authToken
  }

  setTsToken(tsToken: string) {
    localStorage.setItem(AUTH_STORAGE_KEYS.tsToken, tsToken)
  }

  getTsToken(): string {
    const tsToken = localStorage.getItem(AUTH_STORAGE_KEYS.tsToken) as string
    if (!tsToken || tsToken === 'null' || tsToken === 'undefined') {
      return ''
    }
    return tsToken
  }

  /** Verschlüsselte Kennung aus der Übergabe (`?user=…`), roh wie empfangen. */
  setUserParam(userParam: string): void {
    localStorage.setItem(AUTH_STORAGE_KEYS.userParam, userParam)
  }

  getUserParam(): string {
    const value = localStorage.getItem(AUTH_STORAGE_KEYS.userParam)
    if (!value || value === 'null' || value === 'undefined') {
      return ''
    }
    return value
  }

  removeAuthUserToken(): void {
    localStorage.removeItem(AUTH_STORAGE_KEYS.user)
  }

  removeJWTToken(): void {
    localStorage.removeItem(AUTH_STORAGE_KEYS.jwt)
  }

  removeTsToken(): void {
    localStorage.removeItem(AUTH_STORAGE_KEYS.tsToken)
  }

  removeUserParam(): void {
    localStorage.removeItem(AUTH_STORAGE_KEYS.userParam)
  }

  /**
   * Liegt ein Token vor, das noch nicht abgelaufen ist?
   *
   * Grundlage der Navigation: Ohne tragfähigen JWT gibt es keine Seite. Die
   * Ablaufprüfung ist reine Bequemlichkeit, damit der Benutzer nicht erst über
   * einen 401 stolpert. Verbindlich prüft allein das Backend.
   */
  hasValidJwt(): boolean {
    const jwt = this.getJWTToken()
    return !!jwt && !isJwtExpired(jwt)
  }

  isAuthenticated(): boolean {
    const auth = localStorage.getItem(AUTH_STORAGE_KEYS.user)

    try {
      const authObj = JSON.parse(auth) as User

      return !!authObj
    } catch {
      return false
    }
  }

  hasRole(roles: Role[], roleName: RolesEnum): boolean {
    return roles.some((role) => role.name === roleName)
  }
}
